import { useState } from 'react';
import { api, restoreActiveWorkspace, type RevenueWebsite } from '../api';
import { Icon } from './Icon';

/**
 * Install verification, goal tracking and payment-provider setup, written for
 * the person pasting the code: exact URLs with this workspace's id, the events
 * to tick, and the one line that ties a payment to the visit that earned it.
 */

type Provider = 'stripe' | 'lemonsqueezy' | 'polar' | 'shopify' | 'api';

const PROVIDERS: { id: Provider; label: string }[] = [
  { id: 'stripe', label: 'Stripe' },
  { id: 'lemonsqueezy', label: 'LemonSqueezy' },
  { id: 'polar', label: 'Polar' },
  { id: 'shopify', label: 'Shopify' },
  { id: 'api', label: 'Any provider (API)' },
];

function Copyable({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="copyable">
      <pre className="code-block"><code>{text}</code></pre>
      <button
        className="ghost tiny copy-btn"
        onClick={() => {
          void navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1600);
          });
        }}
      >
        <Icon name={copied ? 'check' : 'copy'} size={13} /> {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

export function InstallCheck({ website }: { website: RevenueWebsite }) {
  const [state, setState] = useState<'idle' | 'checking' | 'ok' | 'none' | 'error'>('idle');
  const [count, setCount] = useState(0);
  const check = async () => {
    setState('checking');
    try {
      const { realtime } = await api.getRealtime(website.id);
      setCount(realtime.pageviewsLast30);
      setState(realtime.visitorsLast30 > 0 ? 'ok' : 'none');
    } catch {
      setState('error');
    }
  };
  return (
    <section className="card">
      <header>
        <h2>Verify installation</h2>
        <span className="sub">{website.domain}</span>
      </header>
      <div className="verify-row">
        <button className="primary" onClick={() => void check()} disabled={state === 'checking'}>
          {state === 'checking' ? <span className="spinner" /> : 'Check now'}
        </button>
        {state === 'ok' && (
          <span className="verify ok"><Icon name="check" size={15} /> Receiving data — {count} pageviews in the last 30 minutes.</span>
        )}
        {state === 'none' && (
          <span className="verify none">
            No events in the last 30 minutes. Open {website.domain} in a new tab (ad blockers off), then check again.
          </span>
        )}
        {state === 'error' && <span className="verify none">The check could not run. Try again.</span>}
      </div>
    </section>
  );
}

export function GoalsGuide() {
  return (
    <section className="card">
      <header>
        <h2>Goals and custom events</h2>
        <span className="sub">outbound clicks and file downloads are automatic</span>
      </header>
      <p className="muted">Track any action as a goal. It appears on the Traffic page with conversion rate and revenue, and can be used as a funnel step.</p>
      <Copyable text={`<script>
  // A signup, a demo request, a checkout start — any name you like.
  DataVantage('track', 'Signup', { plan: 'team' });

  // Link the visitor to a known customer (enables cross-device journeys).
  DataVantage('identify', user.id, { email: user.email, name: user.name });
</script>`} />
    </section>
  );
}

export function PaymentIntegrations() {
  const [provider, setProvider] = useState<Provider>('stripe');
  const workspaceId = restoreActiveWorkspace() ?? '<workspace-id>';
  const origin = window.location.origin;
  const hook = (p: string) => `${origin}/api/webhooks/${p}?workspace=${workspaceId}`;

  return (
    <section className="card">
      <header className="breakdown-head">
        <h2>
          <Icon name="revenue" size={15} /> Connect revenue
        </h2>
        <div className="seg" role="tablist" aria-label="Payment provider">
          {PROVIDERS.map((p) => (
            <button
              key={p.id}
              role="tab"
              aria-selected={provider === p.id}
              className={provider === p.id ? 'seg-item on' : 'seg-item'}
              onClick={() => setProvider(p.id)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </header>

      <p className="muted">
        Payments are credited to the visit that earned them, so Traffic shows revenue per source, page and country, and
        Marketing ROI can compute ROAS. Two steps: forward the provider&apos;s webhook, and pass the visitor id through
        checkout.
      </p>

      {provider === 'stripe' && (
        <ol className="setup-steps">
          <li>
            <strong>Add a webhook endpoint</strong> in Stripe → Developers → Webhooks:
            <Copyable text={hook('stripe')} />
            Events: <code>payment_intent.succeeded</code>, <code>customer.subscription.created</code>,{' '}
            <code>customer.subscription.updated</code>, <code>customer.subscription.deleted</code>, <code>charge.refunded</code>.
          </li>
          <li>
            <strong>Set the signing secret</strong> on the server as <code>STRIPE_WEBHOOK_SECRET</code>.
          </li>
          <li>
            <strong>Pass the visitor id</strong> when creating the Checkout Session:
            <Copyable text={`// browser
const visitorId = DataVantage.visitorId();

// server
await stripe.checkout.sessions.create({
  // ...
  metadata: { visitor_hash: visitorId },
  payment_intent_data: { metadata: { visitor_hash: visitorId } },
});`} />
          </li>
        </ol>
      )}

      {provider === 'lemonsqueezy' && (
        <ol className="setup-steps">
          <li>
            <strong>Add a webhook</strong> in LemonSqueezy → Settings → Webhooks:
            <Copyable text={hook('lemonsqueezy')} />
            Events: <code>order_created</code>, <code>order_refunded</code>, <code>subscription_created</code>,{' '}
            <code>subscription_updated</code>, <code>subscription_cancelled</code>.
          </li>
          <li><strong>Set the signing secret</strong> as <code>LEMONSQUEEZY_WEBHOOK_SECRET</code>.</li>
          <li>
            <strong>Pass the visitor id</strong> as checkout custom data:
            <Copyable text={`const url = new URL(checkoutUrl);
url.searchParams.set('checkout[custom][visitor_hash]', DataVantage.visitorId());
window.location.href = url.toString();`} />
          </li>
        </ol>
      )}

      {provider === 'polar' && (
        <ol className="setup-steps">
          <li>
            <strong>Add a webhook</strong> in Polar → Settings → Webhooks (format: raw):
            <Copyable text={hook('polar')} />
            Events: <code>order.created</code>, <code>order.refunded</code>, <code>subscription.created</code>,{' '}
            <code>subscription.updated</code>, <code>subscription.canceled</code>.
          </li>
          <li><strong>Set the secret</strong> as <code>POLAR_WEBHOOK_SECRET</code>.</li>
          <li>
            <strong>Pass the visitor id</strong> as checkout metadata:
            <Copyable text={`await polar.checkouts.create({
  // ...
  metadata: { visitor_hash: visitorIdFromBrowser },
});`} />
          </li>
        </ol>
      )}

      {provider === 'shopify' && (
        <ol className="setup-steps">
          <li>
            <strong>Create a webhook</strong> in Shopify admin → Settings → Notifications → Webhooks, format JSON:
            <Copyable text={hook('shopify')} />
            Topics: <code>Order payment</code> (orders/paid) and <code>Refund create</code> (refunds/create).
          </li>
          <li>
            <strong>Set the secret</strong> shown under the webhook list as <code>SHOPIFY_WEBHOOK_SECRET</code>.
          </li>
          <li>
            <strong>Add the snippet to your theme</strong> (theme.liquid, before <code>&lt;/head&gt;</code>), then store
            the visitor id on the cart so it travels to the order:
            <Copyable text={`<script>
  DataVantage('visitorId', function (id) {
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { dv_visitor_id: id } })
    });
  });
</script>`} />
          </li>
        </ol>
      )}

      {provider === 'api' && (
        <ol className="setup-steps">
          <li>
            <strong>Report each payment</strong> from your server (authenticated with a workspace session or API
            proxy):
            <Copyable text={`POST ${origin}/api/webhooks/payment
Content-Type: application/json

{
  "event_type": "payment_succeeded",
  "external_id": "order_1042",
  "amount": 149.00,
  "currency": "USD",
  "customer_email": "buyer@example.com",
  "visitor_hash": "<DataVantage.visitorId() from the browser>"
}`} />
          </li>
          <li>
            <strong>Or import history</strong> from a CSV on the Revenue page — orders and ad spend both import.
          </li>
        </ol>
      )}
    </section>
  );
}
